import { cn } from '@/shared/utils/cn';
import { TruncatedWithTooltip } from '@/modules/Tooltip';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  getContentAlignClass,
  isFlexColumn,
} from '../../utils/cellStyles';
import { memo, useMemo } from 'react';
import type { DateCellProps } from './DateCell.types';
import { parseDate, formatDate } from './DateCell.utils';

export const DateCell = memo(
  ({
    value,
    column,
    state = 'default',
    className,
    onCellFocus,
    stickyLeft = 0,
    isLastLocked = false,
    dateFormat = 'short',
    locale = 'es-ES',
    fallback = '-',
    isRowEditing,
    hasColumnMaxWidthAuto = false,
  }: DateCellProps) => {
    const baseClasses = getTableCellClasses(
      state,
      className,
      column.locked,
      isLastLocked,
      undefined,
      isRowEditing,
      false,
      column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto)
    );
    const cellProps = getTableCellProps(
      column,
      stickyLeft,
      undefined,
      false,
      hasColumnMaxWidthAuto
    );

    // Obtener formato de la columna si está definido
    const format = column.dateFormat || dateFormat;
    const localeToUse = column.locale || locale;
    const fallbackText = column.dateFallback || fallback;

    const formattedDate = useMemo(() => {
      const parsedDate = parseDate(value);
      if (!parsedDate) return fallbackText;
      return formatDate(parsedDate, format, localeToUse);
    }, [value, format, localeToUse, fallbackText]);

    const hasFixedWidth = column.width !== undefined;
    const alignClass = getContentAlignClass(column.contentAlign);

    // Si hay un render personalizado, usarlo
    if (column.render) {
      return (
        <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
          <div
            className={cn(
              'ui-flex ui-h-full ui-items-center',
              alignClass,
              hasFixedWidth && 'ui-min-w-0 ui-overflow-hidden'
            )}
          >
            {column.render(value, null, column) as React.ReactNode}
          </div>
        </td>
      );
    }

    return (
      <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
        <div
          className={cn(
            'ui-flex ui-h-full ui-items-center ui-text-body-sm-regular',
            alignClass,
            hasFixedWidth && 'ui-min-w-0 ui-overflow-hidden'
          )}
        >
          <TruncatedWithTooltip
            truncate={hasFixedWidth}
            title={formattedDate}
            className={cn(hasFixedWidth && 'ui-block ui-min-w-0')}
          >
            {formattedDate}
          </TruncatedWithTooltip>
        </div>
      </td>
    );
  }
);

DateCell.displayName = 'DateCell';
